import {
    type Room,
    RelationType,
    type MatrixEvent,
    type Thread,
    M_POLL_START,
    RoomEvent,
    type EmptyObject,
    Direction,
} from "matrix-js-sdk/src/matrix";
import { logger as baseLogger } from "matrix-js-sdk/src/logger";
import { isNullOrUndefined } from "matrix-js-sdk/src/utils";

import { type ActionPayload } from "../../dispatcher/payloads";
import { AsyncStoreWithClient } from "../AsyncStoreWithClient";
import defaultDispatcher from "../../dispatcher/dispatcher";
import { MessageEventPreview } from "./previews/MessageEventPreview";
import { PollStartEventPreview } from "./previews/PollStartEventPreview";
import { type TagID } from "../room-list-v3/skip-list/tag";
import { LegacyCallInviteEventPreview } from "./previews/LegacyCallInviteEventPreview";
import { LegacyCallAnswerEventPreview } from "./previews/LegacyCallAnswerEventPreview";
import { LegacyCallHangupEvent } from "./previews/LegacyCallHangupEvent";
import { StickerEventPreview } from "./previews/StickerEventPreview";
import { ReactionEventPreview } from "./previews/ReactionEventPreview";
import { UPDATE_EVENT } from "../AsyncStore";
import { type Preview } from "./previews";
import shouldHideEvent from "../../shouldHideEvent";
import { hasProperty } from "../../dth/utils";
import { ENABLE_NEW_ROOM_LIST } from "../../dth/constants";

const logger = baseLogger.getChild("MessagePreviewStore");

// Emitted event for when a room's preview has changed. First argument will the room for which
// the change happened.
export const ROOM_PREVIEW_CHANGED = "room_preview_changed";

const PREVIEWS: Record<
    string,
    {
        isState: boolean;
        previewer: Preview;
    }
> = {
    "m.room.message": {
        isState: false,
        previewer: new MessageEventPreview(),
    },
    "m.call.invite": {
        isState: false,
        previewer: new LegacyCallInviteEventPreview(),
    },
    "m.call.answer": {
        isState: false,
        previewer: new LegacyCallAnswerEventPreview(),
    },
    "m.call.hangup": {
        isState: false,
        previewer: new LegacyCallHangupEvent(),
    },
    "m.sticker": {
        isState: false,
        previewer: new StickerEventPreview(),
    },
    "m.reaction": {
        isState: false,
        previewer: new ReactionEventPreview(),
    },
    [M_POLL_START.name]: {
        isState: false,
        previewer: new PollStartEventPreview(),
    },
    [M_POLL_START.altName]: {
        isState: false,
        previewer: new PollStartEventPreview(),
    },
};

// The maximum number of events we're willing to look back on to get a preview.
const MAX_EVENTS_BACKWARDS = 50;

// type merging ftw
type TAG_ANY = "im.vector.any";
// eslint-disable-next-line no-redeclare
const TAG_ANY: TAG_ANY = "im.vector.any";

export interface MessagePreview {
    event: MatrixEvent;
    isThreadReply: boolean;
    text: string;
}

const isThreadReply = (event: MatrixEvent): boolean => {
    // a thread root event cannot be a thread reply
    if (event.isThreadRoot) return false;

    const thread = event.getThread();

    // it cannot be a thread reply if there is no thread
    if (!thread) return false;

    const relation = event.getRelation();

    if (
        !!relation &&
        relation.rel_type === RelationType.Annotation &&
        relation.event_id === thread.rootEvent?.getId()
    ) {
        // annotations on the thread root are not a thread reply
        return false;
    }

    return true;
};

const mkMessagePreview = (text: string, event: MatrixEvent): MessagePreview => {
    return {
        event,
        text,
        isThreadReply: isThreadReply(event),
    };
};

export class MessagePreviewStore extends AsyncStoreWithClient<EmptyObject> {
    private static readonly internalInstance = (() => {
        const instance = new MessagePreviewStore();
        instance.start();
        return instance;
    })();

    /**
     * @internal Public for test only
     */
    public static testInstance(): MessagePreviewStore {
        return new MessagePreviewStore();
    }

    // null indicates the preview is empty / irrelevant
    private previews = new Map<string, Map<TagID | TAG_ANY, MessagePreview | null>>();

    // Tracks rooms for which a background fetch is already in progress
    private pendingScrollbacks = new Set<string>();

    // Cache of the latest event timestamp per room, fetched directly via /messages.
    // A value of 0 means the server confirmed no events exist (prevents retries).
    private latestEventTimestampCache = new Map<string, number>();

    private constructor() {
        super(defaultDispatcher, {});
    }

    public static get instance(): MessagePreviewStore {
        return MessagePreviewStore.internalInstance;
    }

    public static getPreviewChangedEventName(room: Room): string {
        return `${ROOM_PREVIEW_CHANGED}:${room?.roomId}`;
    }

    /**
     * Gets the pre-translated preview for a given room
     * @param room The room to get the preview for.
     * @param inTagId The tag ID in which the room resides
     * @returns The preview, or null if none present.
     */
    public async getPreviewForRoom(room: Room, inTagId: TagID): Promise<MessagePreview | null> {
        if (!room) return null; // invalid room, just return nothing

        if (!this.previews.has(room.roomId)) await this.generatePreview(room, inTagId);

        const previews = this.previews.get(room.roomId);
        if (!previews) return null;

        if (previews.has(inTagId)) {
            return previews.get(inTagId)!;
        }
        return previews.get(TAG_ANY) ?? null;
    }

    public generatePreviewForEvent(event: MatrixEvent): string {
        const previewDef = PREVIEWS[event.getType()];
        return previewDef?.previewer.getTextFor(event, undefined, true) ?? "";
    }

    //#region NEW generatePreview IMPLEMENTATION WITH TIMESTAMP RETRIEVAL
    /**
     * Dato un evento, restituisce il timestamp da usare per la preview della stanza.
     * Se l'evento è null, restituisce il timestamp dell'ultimo evento attivo nella stanza.
     * @param room La stanza di cui ottenere il timestamp della preview.
     * @returns Il timestamp da usare per la preview
     */
    public static getLastPreviewEventTimestamp(room: Room): number {
        const event = MessagePreviewStore.findPreviewEvent(room);
        const eventTs = event?.getTs() ?? Number.MIN_SAFE_INTEGER;
        if (eventTs > 0) return eventTs;

        // Fallback: ultimo evento in timeline (qualsiasi tipo, incluse poll response)
        const activeTs = room.getLastActiveTimestamp();
        if (activeTs > 0) return activeTs;

        // Fallback: timestamp cachato via /messages diretto (bypassa il problema delle poll response)
        const cachedTs = MessagePreviewStore.instance.latestEventTimestampCache.get(room.roomId);
        if (cachedTs !== undefined && cachedTs > 0) return cachedTs;

        // Timeline vuota: avvia fetch in background
        MessagePreviewStore.instance.ensureRoomTimeline(room);
        return Number.MIN_SAFE_INTEGER;
    }

    /**
     * Se la timeline della stanza è vuota, recupera il timestamp dell'ultimo evento
     * direttamente via /messages (senza passare per la gestione della timeline del SDK).
     * Questo bypassa il problema per cui le poll response vengono classificate come
     * unknownRelations quando il loro parent non è in timeline, e quindi non vengono
     * mai aggiunte a getLiveTimeline().getEvents().
     */
    public ensureRoomTimeline(room: Room): void {
        if (!this.matrixClient) return;
        if (this.pendingScrollbacks.has(room.roomId)) return;
        if (room.getLiveTimeline().getEvents().length > 0) return;
        // latestEventTimestampCache con valore 0 = server ha confermato che non ci sono eventi
        if (this.latestEventTimestampCache.get(room.roomId) === 0) return;

        logger.debug(`Room ${room.roomId} has empty timeline, fetching latest event timestamp via /messages`);

        this.pendingScrollbacks.add(room.roomId);
        // Usa createMessagesRequest direttamente (from=null = dalla fine, dir=b = un evento recente)
        // Questo bypassa addEventsToTimeline e il problema delle poll response
        this.matrixClient
            .createMessagesRequest(room.roomId, null, 1, Direction.Backward)
            .then((res) => {
                this.pendingScrollbacks.delete(room.roomId);
                const ts = (res.chunk[0] as { origin_server_ts?: number } | undefined)?.origin_server_ts ?? 0;
                // Salva 0 se vuoto per non riprovare; altrimenti salva il timestamp reale
                this.latestEventTimestampCache.set(room.roomId, ts);
                if (ts > 0) {
                    // Emetti l'evento base (senza room ID) per triggerare RoomListStoreV3
                    this.emit(ROOM_PREVIEW_CHANGED, room);
                    // Emetti anche l'evento per-room per eventuali listener granulari
                    this.emit(MessagePreviewStore.getPreviewChangedEventName(room), room);
                    this.emit(UPDATE_EVENT, this);
                }
            })
            .catch(() => {
                this.pendingScrollbacks.delete(room.roomId);
            });
    }

    /**
     * Funzione interna che seleziona l'evento da usare per la preview, secondo la logica esistente.
     * Restituisce l'evento selezionato o null se nessuno trovato.
     */
    private static findPreviewEvent(room: Room): MatrixEvent | null {
        // Ottieni tutti gli eventi live e quelli in attesa dalla timeline della stanza
        // Prendo solo gli ultimi MAX_EVENTS_BACKWARDS eventi per evitare di scorrere tutta la timeline in stanze molto attive
        const events = [...room.getLiveTimeline().getEvents().slice(-MAX_EVENTS_BACKWARDS), ...room.getPendingEvents()];

        const isNewRoomListEnabled = ENABLE_NEW_ROOM_LIST; //SettingsStore.getValue("feature_new_room_list");
        if (!isNewRoomListEnabled) {
            // Aggiungi l'ultima risposta di ogni thread agli eventi
            room.getThreads().forEach((thread: Thread): void => {
                // Prendi l'ultima risposta del thread
                const lastReply = thread.lastReply();
                // Se esiste, aggiungila agli eventi
                if (lastReply) events.push(lastReply);
            });
        }

        // Ordina gli eventi dal più vecchio al più recente
        events.sort((a: MatrixEvent, b: MatrixEvent) => {
            // Confronta i timestamp
            return a.getTs() - b.getTs();
        });

        // Se non ci sono eventi, esci
        if (!events) return null;

        for (let i = events.length - 1; i >= 0; i--) {
            if (i === events.length - MAX_EVENTS_BACKWARDS) {
                // limit reached - clear the preview by breaking out of the loop
                break;
            }

            // Prendi l'evento corrente
            const event = events[i];

            // In questa fase non ci serve decriptare perchè serve solo prendere l'ultimo evento visibile 
            // Decripta l'evento se necessario
            // await this.matrixClient?.decryptEventIfNeeded(event);

            // Verifica se l'evento deve essere nascosto
            const shouldHide = shouldHideEvent(event);
            // Se deve essere nascosto, passa al prossimo
            if (shouldHide) continue;
            // Prendi il tipo di preview per questo evento
            const previewDef = PREVIEWS[event.getType()];
            // Se non c'è un previewer, passa al prossimo
            if (!previewDef) continue;
            // Se è evento di stato ma manca la stateKey, passa
            if (previewDef.isState && isNullOrUndefined(event.getStateKey())) continue;

            // Ottieni il testo della preview
            const anyPreviewText = previewDef.previewer.getTextFor(event);
            // Se non è previewable, passa al prossimo
            if (!anyPreviewText) continue;

            // Se siamo qui, abbiamo trovato l'evento da usare per la preview, restituiscilo
            return event;
        }
        // Se non è stata generata nessuna preview, restituisci null
        return null;
    }

    /**
     * Genera e aggiorna la preview dei messaggi per una stanza e un tag specifico.
     * Scorre gli eventi della stanza (inclusi pending e ultime risposte ai thread),
     * trova il primo evento valido per la preview e aggiorna la mappa delle preview.
     * Se non trova eventi validi, svuota la preview.
     *
     * @param room La stanza Matrix per cui generare la preview.
     * @param tagId (Opzionale) Il tag per cui generare la preview.
     */
    private async generatePreview(room: Room, tagId?: TagID): Promise<void> {
        // Trova l'evento da usare per la preview secondo la logica esistente
        const event = MessagePreviewStore.findPreviewEvent(room);

        // Recupera o crea la mappa delle preview per la stanza
        let map = this.previews.get(room.roomId);
        if (!map) {
            // Nuova mappa se non esiste
            map = new Map<TagID | TAG_ANY, MessagePreview | null>();
            // Salva la nuova mappa
            this.previews.set(room.roomId, map);
        }

        // Imposta i tag da generare (TAG_ANY sempre, e tagId se fornito)
        // Assicurati che TAG_ANY sia presente
        if (!map.has(TAG_ANY)) map.set(TAG_ANY, null);
        // Se c'è un tagId, aggiungilo se manca
        if (tagId && !map.has(tagId)) map.set(tagId, null);

        // Flag per sapere se la preview è cambiata
        let changed = false;
        // Scorri gli eventi dal più recente al più vecchio
        if (event) {
            // Decripta l'evento se necessario
            await this.matrixClient?.decryptEventIfNeeded(event);
            // Prendi il tipo di preview per questo evento
            const previewDef = PREVIEWS[event.getType()];
            // Ottieni il testo della preview
            const anyPreviewText = previewDef?.previewer.getTextFor(event);
            // Se arriva qui deve essere previewable, ma mettiamo un controllo di sicurezza
            if (anyPreviewText) {
                // Se il testo della preview è cambiato, aggiorna il flag
                changed = changed || anyPreviewText !== map.get(TAG_ANY)?.text;
                // Aggiorna la preview per TAG_ANY
                map.set(TAG_ANY, mkMessagePreview(anyPreviewText, event));

                // Genera le preview per tutti i tag diversi da TAG_ANY
                // Prendi tutti i tag tranne TAG_ANY
                const tagsToGenerate = Array.from(map.keys()).filter((t) => t !== TAG_ANY);
                for (const genTagId of tagsToGenerate) {
                    // Calcola il vero tagId
                    const realTagId = genTagId === TAG_ANY ? undefined : genTagId;
                    // Ottieni la preview per il tag
                    const preview = previewDef.previewer.getTextFor(event, realTagId);

                    if (preview === anyPreviewText) {
                        // Se la preview per il tag è uguale a quella generica, elimina la preview specifica
                        changed = changed || anyPreviewText !== map.get(genTagId)?.text;
                        map.delete(genTagId);
                    } else {
                        // Altrimenti aggiorna la preview per il tag specifico
                        changed = changed || preview !== map.get(genTagId)?.text;
                        map.set(genTagId, preview ? mkMessagePreview(anyPreviewText, event) : null);
                    }
                }
            }

            if (changed) {
                // Se la preview è cambiata, aggiorna la mappa e notifica il cambiamento
                this.previews.set(room.roomId, map);
                this.emit(UPDATE_EVENT, this);
                this.emit(MessagePreviewStore.getPreviewChangedEventName(room), room);
            }
            // Siamo a posto, esci dalla funzione
            return;
        }

        // Se non è stata generata nessuna preview, svuota la mappa delle preview per la stanza
        this.previews.set(room.roomId, new Map<TagID | TAG_ANY, MessagePreview | null>());
        this.emit(UPDATE_EVENT, this);
        this.emit(MessagePreviewStore.getPreviewChangedEventName(room), room);
    }

    // /**
    //  * Genera e aggiorna la preview dei messaggi per una stanza e un tag specifico.
    //  * Scorre gli eventi della stanza (inclusi pending e ultime risposte ai thread),
    //  * trova il primo evento valido per la preview e aggiorna la mappa delle preview.
    //  * Se non trova eventi validi, svuota la preview.
    //  *
    //  * @param room La stanza Matrix per cui generare la preview.
    //  * @param tagId (Opzionale) Il tag per cui generare la preview.
    //  */
    // private async generatePreviewOld(room: Room, tagId?: TagID): Promise<void> {
    //     const events = [...room.getLiveTimeline().getEvents(), ...room.getPendingEvents()];

    //     const isNewRoomListEnabled = SettingsStore.getValue("feature_new_room_list");
    //     if (!isNewRoomListEnabled) {
    //         // add last reply from each thread
    //         room.getThreads().forEach((thread: Thread): void => {
    //             const lastReply = thread.lastReply();
    //             if (lastReply) events.push(lastReply);
    //         });
    //     }

    //     // sort events from oldest to newest
    //     events.sort((a: MatrixEvent, b: MatrixEvent) => {
    //         return a.getTs() - b.getTs();
    //     });

    //     if (!events) return; // should only happen in tests

    //     let map = this.previews.get(room.roomId);
    //     if (!map) {
    //         map = new Map<TagID | TAG_ANY, MessagePreview | null>();
    //         this.previews.set(room.roomId, map);
    //     }

    //     // Set the tags so we know what to generate
    //     if (!map.has(TAG_ANY)) map.set(TAG_ANY, null);
    //     if (tagId && !map.has(tagId)) map.set(tagId, null);

    //     let changed = false;
    //     for (let i = events.length - 1; i >= 0; i--) {
    //         if (i === events.length - MAX_EVENTS_BACKWARDS) {
    //             // limit reached - clear the preview by breaking out of the loop
    //             break;
    //         }

    //         const event = events[i];

    //         await this.matrixClient?.decryptEventIfNeeded(event);
    //         const shouldHide = shouldHideEvent(event);
    //         if (shouldHide) continue;
    //         const previewDef = PREVIEWS[event.getType()];
    //         if (!previewDef) continue;
    //         if (previewDef.isState && isNullOrUndefined(event.getStateKey())) continue;

    //         const anyPreviewText = previewDef.previewer.getTextFor(event);
    //         if (!anyPreviewText) continue; // not previewable for some reason

    //         changed = changed || anyPreviewText !== map.get(TAG_ANY)?.text;
    //         map.set(TAG_ANY, mkMessagePreview(anyPreviewText, event));

    //         const tagsToGenerate = Array.from(map.keys()).filter((t) => t !== TAG_ANY); // we did the any tag above
    //         for (const genTagId of tagsToGenerate) {
    //             const realTagId = genTagId === TAG_ANY ? undefined : genTagId;
    //             const preview = previewDef.previewer.getTextFor(event, realTagId);

    //             if (preview === anyPreviewText) {
    //                 changed = changed || anyPreviewText !== map.get(genTagId)?.text;
    //                 map.delete(genTagId);
    //             } else {
    //                 changed = changed || preview !== map.get(genTagId)?.text;
    //                 map.set(genTagId, preview ? mkMessagePreview(anyPreviewText, event) : null);
    //             }
    //         }

    //         if (changed) {
    //             // We've muted the underlying Map, so just emit that we've changed.
    //             this.previews.set(room.roomId, map);
    //             this.emit(UPDATE_EVENT, this);
    //             this.emit(MessagePreviewStore.getPreviewChangedEventName(room), room);
    //         }
    //         return; // we're done
    //     }

    //     // At this point, we didn't generate a preview so clear it
    //     this.previews.set(room.roomId, new Map<TagID | TAG_ANY, MessagePreview | null>());
    //     this.emit(UPDATE_EVENT, this);
    //     this.emit(MessagePreviewStore.getPreviewChangedEventName(room), room);
    // }

    //#endregion

    protected async onAction(payload: ActionPayload): Promise<void> {
        if (!this.matrixClient) return;

        if (payload.action === "MatrixActions.Room.timeline" || payload.action === "MatrixActions.Event.decrypted") {
            const event = payload.event; // TODO: Type out the dispatcher
            const roomId = event.getRoomId();
            const isHistoricalEvent = hasProperty(payload, "isLiveEvent") && !payload.isLiveEvent;

            if (!roomId || !this.previews.has(roomId) || isHistoricalEvent) return;

            const room = this.matrixClient.getRoom(roomId);

            if (!room) return;

            await this.generatePreview(room, TAG_ANY);
        }
    }

    protected async onReady(): Promise<void> {
        if (!this.matrixClient) return;
        this.matrixClient.on(RoomEvent.LocalEchoUpdated, this.onLocalEchoUpdated);
    }

    protected async onNotReady(): Promise<void> {
        if (!this.matrixClient) return;
        this.matrixClient.off(RoomEvent.LocalEchoUpdated, this.onLocalEchoUpdated);
    }

    protected onLocalEchoUpdated = async (ev: MatrixEvent, room: Room): Promise<void> => {
        if (!this.previews.has(room.roomId)) return;
        await this.generatePreview(room, TAG_ANY);
    };
}
